/**
 * Step4Details Component
 *
 * Collects:
 * - License state (state dropdown with cross-field comparison to mailing address)
 * - Marital status (select: Single / Married / Domestic Partner / Divorced / Widowed)
 * - Job title (text input)
 * - Employer name (text input)
 * - Student status (toggle checkbox)
 * - If student: school name, program, full-time/part-time (radio)
 *
 * Shows cross-field warnings if license state differs from mailing address state.
 * Shows age milestone banner if user is 25 or 26.
 * Uses react-hook-form's useFormContext and Controller for managed state.
 * Validates against step4Schema from formSchemas.
 */

import { useFormContext, Controller } from 'react-hook-form';
import { Emery } from '@/components/ui/Calla';
import { EMERY_MESSAGES } from '@/constants/callaMessages';
import { FormField } from '@/components/ui/FormField';
import { AnimatedReveal } from '@/components/ui/AnimatedReveal';
import { InfoBanner } from '@/components/ui/InfoBanner';
import { getCrossFieldWarnings, EDUCATION_LEVELS, EDUCATION_LABELS, type FullFormData } from '@/schemas/formSchemas';
import { US_STATES } from '@/constants/states';
import { SearchableSelect } from '@/components/ui/SearchableSelect';

export default function Step4Details() {
  const { control, formState: { errors }, watch } = useFormContext<FullFormData>();
  const isStudent = watch('step4.isStudent');
  const formValues = watch();

  // Get cross-field warnings
  const warnings = getCrossFieldWarnings({
    ...formValues.step2,
    ...formValues.step4,
  });
  const licenseWarnings = warnings.filter((w) => w.field === 'licenseState');
  const ageWarnings = warnings.filter((w) => w.field === 'dateOfBirth');

  return (
    <div>
      <Emery message={EMERY_MESSAGES.step4} />

      {/* License State */}
      <FormField
        label="Driver's License State"
        name="step4.licenseState"
        required={true}
        error={errors.step4?.licenseState?.message}
        whyText="Your license state affects your rates and determines which state's regulations apply."
      >
        <Controller
          name="step4.licenseState"
          control={control}
          render={({ field }) => (
            <SearchableSelect
              options={US_STATES.map((s) => ({ value: s.code, label: s.name }))}
              value={field.value}
              onChange={field.onChange}
              placeholder="Search or select state"
              error={!!errors.step4?.licenseState}
            />
          )}
        />
      </FormField>

      {/* Year First Licensed */}
      <FormField
        label="What year were you first licensed?"
        name="step4.yearFirstLicensed"
        required={true}
        error={errors.step4?.yearFirstLicensed?.message}
        helperText="e.g., the year you got your license"
      >
        <Controller
          name="step4.yearFirstLicensed"
          control={control}
          render={({ field }) => (
            <input
              {...field}
              type="text"
              inputMode="numeric"
              pattern="[0-9]*"
              maxLength={4}
              autoComplete="off"
              enterKeyHint="next"
              placeholder="2015"
              className={`form-input ${
                errors.step4?.yearFirstLicensed ? 'form-input-error' : ''
              }`}
              onChange={(e) => field.onChange(e.target.value ? parseInt(e.target.value) : '')}
            />
          )}
        />
      </FormField>

      {/* Cross-field warning: license state differs from mailing address */}
      <AnimatedReveal isVisible={licenseWarnings.length > 0}>
        <div className="mb-6">
          {licenseWarnings.map((warning, idx) => (
            <InfoBanner
              key={idx}
              type={warning.type as 'info' | 'savings_opportunity' | 'warning'}
              message={warning.message}
            />
          ))}
        </div>
      </AnimatedReveal>

      {/* Age milestone banner */}
      <AnimatedReveal isVisible={ageWarnings.length > 0}>
        <div className="mb-6">
          {ageWarnings.map((warning, idx) => (
            <InfoBanner
              key={idx}
              type={warning.type as 'info' | 'savings_opportunity' | 'warning'}
              message={warning.message}
            />
          ))}
        </div>
      </AnimatedReveal>

      {/* Marital Status */}
      <FormField
        label="Marital Status"
        name="step4.maritalStatus"
        required={true}
        error={errors.step4?.maritalStatus?.message}
        whyText="Marital status can affect your rates and coverage options."
      >
        <Controller
          name="step4.maritalStatus"
          control={control}
          render={({ field }) => (
            <select
              {...field}
              className={`form-input ${
                errors.step4?.maritalStatus ? 'form-input-error' : ''
              }`}
            >
              <option value="">Select status</option>
              <option value="divorced">Divorced</option>
              <option value="domestic_partner">Domestic Partner</option>
              <option value="married">Married</option>
              <option value="single">Single</option>
              <option value="widowed">Widowed</option>
            </select>
          )}
        />
      </FormField>

      {/* Education Level */}
      <FormField
        label="Highest level of education completed"
        name="step4.educationLevel"
        required={true}
        error={errors.step4?.educationLevel?.message}
      >
        <Controller
          name="step4.educationLevel"
          control={control}
          render={({ field }) => (
            <select
              {...field}
              className={`form-input ${
                errors.step4?.educationLevel ? 'form-input-error' : ''
              }`}
            >
              <option value="">Select education level</option>
              {EDUCATION_LEVELS.map((level) => (
                <option key={level} value={level}>
                  {EDUCATION_LABELS[level]}
                </option>
              ))}
            </select>
          )}
        />
      </FormField>

      {/* Job Title */}
      <FormField
        label="Job Title"
        name="step4.jobTitle"
        required={true}
        error={errors.step4?.jobTitle?.message}
        helperText="Your current occupation"
        whyText="Some occupations qualify for occupational discounts."
      >
        <Controller
          name="step4.jobTitle"
          control={control}
          render={({ field }) => (
            <input
              {...field}
              type="text"
              autoComplete="organization-title"
              autoCapitalize="words"
              enterKeyHint="next"
              placeholder="e.g., Teacher, Accountant"
              className={`form-input ${
                errors.step4?.jobTitle ? 'form-input-error' : ''
              }`}
            />
          )}
        />
      </FormField>

      {/* Employer Name */}
      <FormField
        label="Employer Name"
        name="step4.employerName"
        required={true}
        error={errors.step4?.employerName?.message}
        helperText="Company or organization name"
        whyText="Some employers partner with insurers for group discounts."
      >
        <Controller
          name="step4.employerName"
          control={control}
          render={({ field }) => (
            <input
              {...field}
              type="text"
              autoComplete="organization"
              autoCapitalize="words"
              enterKeyHint="next"
              placeholder="e.g., ABC Company, Self-employed"
              className={`form-input ${
                errors.step4?.employerName ? 'form-input-error' : ''
              }`}
            />
          )}
        />
      </FormField>

      {/* Student Status Toggle */}
      <div className="mb-6">
        <label className="flex items-center gap-3 cursor-pointer">
          <Controller
            name="step4.isStudent"
            control={control}
            render={({ field: { value, ...field } }) => (
              <input
                {...field}
                type="checkbox"
                checked={value}
                onChange={(e) => field.onChange(e.target.checked)}
                className="w-4 h-4 rounded border-gray-300 text-brand-primary focus:ring-brand-primary cursor-pointer"
              />
            )}
          />
          <span className="text-sm font-medium text-gray-700">
            I am a student
          </span>
        </label>
      </div>

      {/* Conditional: Student Fields */}
      <AnimatedReveal isVisible={isStudent}>
        <div className="space-y-6 border-l-4 border-brand-primary/30 pl-4 py-2">
          {/* School Name */}
          <FormField
            label="School Name"
            name="step4.studentSchool"
            required={true}
            error={errors.step4?.studentSchool?.message}
            helperText="University or college name"
          >
            <Controller
              name="step4.studentSchool"
              control={control}
              render={({ field }) => (
                <input
                  {...field}
                  type="text"
                  autoComplete="organization"
                  autoCapitalize="words"
                  enterKeyHint="next"
                  placeholder="e.g., State University"
                  className={`form-input ${
                    errors.step4?.studentSchool ? 'form-input-error' : ''
                  }`}
                />
              )}
            />
          </FormField>

          {/* Program / Major */}
          <FormField
            label="Program / Major (Optional)"
            name="step4.studentProgram"
            required={false}
            error={errors.step4?.studentProgram?.message}
          >
            <Controller
              name="step4.studentProgram"
              control={control}
              render={({ field }) => (
                <input
                  {...field}
                  type="text"
                  autoComplete="off"
                  autoCapitalize="words"
                  enterKeyHint="next"
                  placeholder="e.g., Business Administration"
                  className="form-input"
                />
              )}
            />
          </FormField>

          {/* Degree Pursuing */}
          <FormField
            label="Degree you are pursuing"
            name="step4.studentDegreePursuing"
            required={true}
            error={errors.step4?.studentDegreePursuing?.message}
          >
            <Controller
              name="step4.studentDegreePursuing"
              control={control}
              render={({ field }) => (
                <input
                  {...field}
                  type="text"
                  autoComplete="off"
                  autoCapitalize="words"
                  enterKeyHint="next"
                  placeholder="e.g., Bachelor of Science"
                  className={`form-input ${
                    errors.step4?.studentDegreePursuing ? 'form-input-error' : ''
                  }`}
                />
              )}
            />
          </FormField>

          {/* Full-time / Part-time */}
          <div className="mb-6">
            <label className="form-label">Enrollment Status</label>
            <div className="mt-2 flex gap-4">
              <label className="flex items-center gap-2 cursor-pointer">
                <Controller
                  name="step4.studentFullTime"
                  control={control}
                  render={({ field: { value, ...field } }) => (
                    <input
                      {...field}
                      type="radio"
                      value="true"
                      checked={value === true}
                      onChange={() => field.onChange(true)}
                      className="w-4 h-4 text-brand-primary focus:ring-brand-primary cursor-pointer"
                    />
                  )}
                />
                <span className="text-sm font-medium text-gray-700">Full-time</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <Controller
                  name="step4.studentFullTime"
                  control={control}
                  render={({ field: { value, ...field } }) => (
                    <input
                      {...field}
                      type="radio"
                      value="false"
                      checked={value === false}
                      onChange={() => field.onChange(false)}
                      className="w-4 h-4 text-brand-primary focus:ring-brand-primary cursor-pointer"
                    />
                  )}
                />
                <span className="text-sm font-medium text-gray-700">Part-time</span>
              </label>
            </div>
          </div>
        </div>
      </AnimatedReveal>
    </div>
  );
}
